import axios from 'axios';
import { getEnvironmentConfig } from '../config/apiConfig';
import { getValidAdminToken, adminLogout, clearAdminLocalStorage } from '../auth/authManager';
import { discoverAndValidateEndpoints } from '../endpoints/endpointManager';
import { authServiceRequest, adminRequest } from './baseAPI';

// Authenticate with central auth service and get available endpoints
const authenticateAndGetEndpoints = async (encryptedKey) => {
  console.log('🔐 authenticateAndGetEndpoints called with encryptedKey:', !!encryptedKey);
  try {
    console.log('Authenticating with central auth service:', getEnvironmentConfig().authServiceEndpoint);
    
    // Step 1: Authenticate with central auth service (no API prefix for hardcoded auth service)
    const authResponse = await axios.post(`${getEnvironmentConfig().authServiceEndpoint}/api/admin/auth/login`, {
      encryptedKey
    }, {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getEnvironmentConfig().apiConfig.ADMIN_API_KEY
      },
      timeout: 10000
    });

    console.log('Auth service response:', authResponse.data);

    // Check if authentication was successful
    // Server returns message: "Admin login successful" on success
    if (authResponse.data.message !== 'Admin login successful' && !authResponse.data.token) {
      throw new Error(authResponse.data.message || 'Authentication failed');
    }

    // Step 2: Store token temporarily for endpoint discovery
    const tempToken = authResponse.data.token;
    localStorage.setItem('admin-token', tempToken);
    localStorage.setItem('admin-session', JSON.stringify({
      sessionId: authResponse.data.sessionId,
      authorizedIps: authResponse.data.authorizedIps || [],
      currentIp: authResponse.data.currentIp,
      expiresAt: authResponse.data.expiresAt
    }));

    // Step 3: Get endpoints from auth service /api/admin/endpoints
    let availableEndpoints = [];
    let configEndpoint = '';
    console.log('🔍 Calling /api/admin/endpoints...');
    try {
      const authServiceUrl = getEnvironmentConfig().authServiceEndpoint;
      console.log('🔍 Auth service URL:', authServiceUrl);
      console.log('🔍 Token:', tempToken ? tempToken.substring(0, 20) + '...' : 'missing');
      
      const endpointsResponse = await axios.get(`${authServiceUrl}/api/admin/endpoints`, {
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'x-api-key': getEnvironmentConfig().apiConfig.ADMIN_API_KEY
        },
        timeout: 10000
      });
      
      console.log('🔍 Endpoints response status:', endpointsResponse.status);
      console.log('🔍 Endpoints response data:', endpointsResponse.data);
      
      availableEndpoints = endpointsResponse.data.endpoints || [];
      console.log('Endpoints from auth service:', availableEndpoints);
      
      if (availableEndpoints.length > 0) {
        const firstEndpoint = availableEndpoints[0].baseUrl || availableEndpoints[0].url || '';
        if (firstEndpoint) {
          configEndpoint = firstEndpoint;
          localStorage.setItem('config-service-endpoint', JSON.stringify({ 
            url: configEndpoint, 
            updatedAt: new Date().toISOString() 
          }));
          localStorage.setItem('available-endpoints', JSON.stringify(availableEndpoints));
          console.log('Config service endpoint set to:', configEndpoint);
        }
      }
    } catch (endpointsError) {
      console.error('❌ Failed to get endpoints from auth service:');
      console.error('❌ Error message:', endpointsError.message);
      console.error('❌ Error response:', endpointsError.response?.data || 'No response');
      console.error('❌ Error status:', endpointsError.response?.status || 'No status');
    }
    
    // Step 4: Discover and validate endpoints from endpoint management
    let endpointDiscovery;
    try {
      endpointDiscovery = await discoverAndValidateEndpoints();
    } catch (endpointError) {
      console.warn('Endpoint discovery failed:', endpointError.message);
      
      // Use endpoints from auth service as fallback
      endpointDiscovery = {
        success: availableEndpoints.length > 0,
        primaryEndpoint: configEndpoint ? { url: configEndpoint } : null,
        healthyEndpoints: configEndpoint ? [{ url: configEndpoint, isHealthy: true }] : [],
        allEndpoints: availableEndpoints,
        hasPrimary: !!configEndpoint,
        error: endpointError.message
      };
    }

    return {
      success: true,
      token: tempToken,
      sessionInfo: {
        sessionId: authResponse.data.sessionId,
        authorizedIps: authResponse.data.authorizedIps || [],
        currentIp: authResponse.data.currentIp,
        expiresAt: authResponse.data.expiresAt
      },
      endpoints: endpointDiscovery.allEndpoints,
      primaryEndpoint: endpointDiscovery.primaryEndpoint,
      hasPrimary: endpointDiscovery.hasPrimary
    };

  } catch (error) {
    console.error('Authentication and endpoint discovery failed:', error);
    throw error;
  }
};

export const authAPI = {
  // IP-based authentication with endpoint discovery
  login: async (encryptedKey) => {
    try {
      console.log('Starting IP-based authentication with endpoint discovery...');
      
      const result = await authenticateAndGetEndpoints(encryptedKey);
      
      if (result.success) {
        console.log('Authentication successful, storing token and session info');
        localStorage.setItem('admin-token', result.token);
        localStorage.setItem('admin-session', JSON.stringify(result.sessionInfo));
        
        return { 
          success: true, 
          data: result,
          sessionInfo: result.sessionInfo,
          endpoints: result.endpoints
        };
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('Login request failed:', error);
      // Clear any partial auth state using comprehensive cleanup
      clearAdminLocalStorage();
      throw error;
    }
  },

  logout: () => {
    adminLogout();
  },

  checkAuth: async () => {
    const token = getValidAdminToken();
    if (!token) {
      throw new Error('No valid token found');
    }
    
    try {
      const response = await authServiceRequest('/auth/verify');
      return response.data;
    } catch (error) {
      adminLogout();
      throw error;
    }
  },

  // Session management endpoints
  getSession: async () => {
    try {
      const response = await authServiceRequest('/auth/session');
      return response.data;
    } catch (error) {
      console.error('Failed to get session info:', error);
      throw error;
    }
  },

  addCurrentIp: async (ip) => {
    try {
      const response = await authServiceRequest('/auth/session/add-ip', {
        method: 'POST',
        data: { ip }
      });

      if (response.data) {
        // Update stored session info
        const session = JSON.parse(localStorage.getItem('admin-session') || '{}');
        session.authorizedIps = response.data.authorizedIps || [];
        localStorage.setItem('admin-session', JSON.stringify(session));

        // Dispatch event for UI updates
        window.dispatchEvent(new CustomEvent('session-updated', {
          detail: session
        }));
      }

      return response.data;
    } catch (error) {
      console.error('Failed to add current IP:', error);
      throw error;
    }
  },

  removeAuthorizedIp: async (ip) => {
    try {
      const response = await authServiceRequest(`/auth/session/remove-ip/${encodeURIComponent(ip)}`, {
        method: 'DELETE'
      });

      if (response.data) {
        // Update stored session info
        const session = JSON.parse(localStorage.getItem('admin-session') || '{}');
        session.authorizedIps = response.data.authorizedIps || [];
        localStorage.setItem('admin-session', JSON.stringify(session));

        // Dispatch event for UI updates
        window.dispatchEvent(new CustomEvent('session-updated', {
          detail: session
        }));
      }

      return response.data;
    } catch (error) {
      console.error('Failed to remove authorized IP:', error);
      throw error;
    }
  },

  getAllSessions: async () => {
    try {
      const response = await authServiceRequest('/auth/sessions');
      return response.data;
    } catch (error) {
      console.error('Failed to get all sessions:', error);
      throw error;
    }
  },

  revokeSession: async (sessionId) => {
    try {
      const response = await authServiceRequest(`/auth/sessions/${sessionId}`, {
        method: 'DELETE'
      });
      return response.data;
    } catch (error) {
      console.error('Failed to revoke session:', error);
      throw error;
    }
  },

  // Refresh endpoints from auth service
  refreshEndpoints: async () => {
    try {
      const token = getValidAdminToken();
      if (!token) {
        throw new Error('No valid token found');
      }

      // Call hardcoded auth service without API prefix
      const response = await axios.get(`${getEnvironmentConfig().authServiceEndpoint}/api/admin/endpoints`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-api-key': getEnvironmentConfig().apiConfig.ADMIN_API_KEY
        },
        timeout: 10000
      });

      const availableEndpoints = response.data.endpoints || [];
      
      if (availableEndpoints.length === 0) {
        throw new Error('No endpoints available from auth service');
      }
      
      // Store in localStorage
      localStorage.setItem('available-endpoints', JSON.stringify(availableEndpoints));

      console.log('Endpoints refreshed:', availableEndpoints);
      return availableEndpoints;
    } catch (error) {
      console.error('Failed to refresh endpoints:', error);
      throw error;
    }
  }
}; 